// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ### multipleNoteSolfegeID.ts
// ##  Identify several notes in scale degrees or solfege
//
import { utils } from 'shared/src';
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { ModeAlias } from '../../uTheory.Notation/scale';
import Scale from '../../uTheory.Notation/scale';
import type { UserData } from '../interactivePrototype';
import { compareDegrees } from '../oneNoteSolfegeID';
import type { SolfegeQuestionObject } from '../../uTheory.Skills/solfegeQuestionGenerator';
import SolfegeQuestionGenerator from '../../uTheory.Skills/solfegeQuestionGenerator';
import * as Font from '../../uTheory.Font/font';
import SolfegeSystem from '../../uTheory.Notation/solfegeSystem';
import type { AnswerAndData } from '../interactiveQuestionPrototype';
import { MultipleNoteSolfegeDictationInteractivePrototype } from '../sharedSolfegeIdMethods';
import type { InteractiveRegistryEntry } from '../../uTheory.Skills/interactiveRegistry';
import type { MultipleNoteSolfegeIDOptions } from '../multipleNoteSolfegeID';
import type { MultipleNoteSolfegeStaffIDOptions } from '../multipleNoteSolfegeStaffID';
import { wrongOctaveOnly } from '../multipleNoteSolfegeStaffID';
import KeySignature from '../../uTheory.Notation/keySignature';
import TimeSignature from '../../uTheory.Notation/timeSignature';
import Music from '../../uTheory.Notation/music';
import type Note from '../../uTheory.Notation/note';
import Staff from '../../uTheory.Notation/staff';
import Voice from '../../uTheory.Notation/voice';
import NoteHolder from '../../uTheory.Notation/noteHolder';
import Clef from '../../uTheory.Notation/clef';
import Measure from '../../uTheory.Notation/measure';
import Editable from '../../uTheory.Notation/editable';
import { Annotation } from '../../vex';
import type { ScaleDegree } from '../solfegeDefinitions';

type AnswerStatus = boolean | 'octaveDisplacement' | 'accidental' | 'low' | 'high';

export default class SolfegeThenStaffDictation extends MultipleNoteSolfegeDictationInteractivePrototype {
  declare questions: SolfegeQuestionObject[];

  declare currentQuestion: SolfegeQuestionObject;

  declare options: MultipleNoteSolfegeStaffIDOptions;

  protected answerDiv: HTMLDivElement;

  protected currentKey: KeySignature;

  private mode: 'solfege' | 'staff' | 'gameOver' = 'solfege';

  /** Array containing which notes in the currentQuestion has been answered correctly (wrong octave counted as correct)  */
  protected priorCorrect: boolean[];

  /** Array of feedback strings that records the feedback for each noteHolder */
  protected feedback: string[] = [];

  staves: {
    // NOT protected so that this can be accessed in tests.
    staff: Staff;
    voice: Voice;
    noteHolders: NoteHolder[];
    clef: Clef;
  }[];

  constructor(displayDiv: HTMLDivElement, options: MultipleNoteSolfegeIDOptions, userData?: any) {
    // copy options so they don't get rewritten on themselves...
    options = { ...options };
    super(displayDiv, options, userData);
    this.setSolfegeSystem();
    this.targetSpeedFromSkills = SolfegeThenStaffDictation.registry[0].skills;
    this.init(displayDiv, options, userData);
  }

  protected setQuestionPrompt() {
    this.questionPrompt =
      this.mode === 'solfege'
        ? `Identify the ${this.solfegeSystem} you hear.`
        : `Now write the ${this.solfegeSystem} on the staff.`;
    if (!this.optionsTrue('testMode')) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions.length}`;
    }
    this.displayInstructions(true);
    this.resize();
  }

  init(displayDiv: HTMLDivElement, options: MultipleNoteSolfegeIDOptions, userData?: UserData) {
    if (options.testMode) {
      const err = Error('Test mode is not available yet on SolfegeThenStaffDictation.');
      this.loadError(err);
      throw err;
    }

    options.maxQuestions = options.maxQuestions || 8;
    this.generator = new SolfegeQuestionGenerator();

    if (typeof options.level !== 'number') {
      // We need a level for showing the right buttons, and we should
      // do everything in our power to figure it out if it's not given.
      if (options.levelFromUserData) {
        options.level = this.getLevel(userData, 'multiple');
      } else if (options.skills && !options.hasOwnProperty('level')) {
        options.level = this.setLevelFromSkill(options);
      } else if (options.hasOwnProperty('level') && !options.skills) {
        options.skills = [this.getSkillFromLevel(options)];
      } else if (userData && options.drill) {
        options.level = this.getLevel(userData, 'multiple');
      } else {
        throw new Error(
          'options.skills or options.level must be provided in manual mode to configure which buttons are visible.'
        );
      }
    }

    // Set default notesPerQuestion:
    if (options.drill && !options.notesPerQuestion) {
      options.notesPerQuestion = 4;
    }

    this.settings = {
      loadAudioFirst: true,
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a chord progression to establish ' +
          ' the key, followed by several notes in that key.</p>' +
          '<p>Click the solfege/scale degrees of the notes you hear.</p>',
        callback: this.startGame,
        thisArg: this,
      },
      instructions: true,
      editor: {
        fixedWidth: 220,
        maxMeasurePadding: 400,
        ariaTitle: () => {
          const ariaString = this.music
            ? this.music.toAriaString({
                rhythm: false,
                barline: false,
                keySignature: { accidentalsCount: true, accidentalsNames: false },
              })
            : '';
          const selected = this.editor && this.editor.getSelected();
          const selectedText =
            selected && selected[0]
              ? selected[0].toAriaString({
                  rhythm: false,
                })
              : '';
          return `${selectedText} ${ariaString}`;
        },
        disableTextInput: true,
      },
      notation: false,
      piano: false,
      letters: {
        accidentalButtons: { noDoubleTap: true },
        solfege: { system: this.system, quality: 'Major' },
        customButtons: [
          { tagName: 'br', variable: 'afterAccidentalsBR' },
          {
            text: 'Play Tonic',
            value: 'playTonic',
            variable: 'playTonic',
          },
          { tagName: 'br', perspective: ['tall', 'portrait', 'square'] },
          { text: 'Play Notes', value: 'play', variable: 'playNotes' },
          { tagName: 'br' },
          { text: 'Check', value: 'check', hotKey: ['enter'], variable: 'checkAnswerButton' },
          { tagName: 'br', variable: 'checkAnswerBR' },
        ],
      },
      checkButton: false,
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    if (
      options.testMode &&
      typeof this.settings.letters === 'object' &&
      Array.isArray(this.settings.letters.customButtons)
    ) {
      this.settings.letters.customButtons.push({
        text: Font.toHTML('symbol', 'trash'),
        value: 'delete',
        hotKey: ['backspace', 'del', 'ctrl+z', 'command+z'],
      });
      this.settings.letters.customButtons.push({
        text: 'Check',
        value: 'check',
        hotKey: ['enter'],
      });
      this.settings.letters.customButtons.push({
        tagName: 'br',
      });
    }

    return super.init(displayDiv, options, userData).then(() => {
      this.editor.renderer.addHook('afterRedraw', () => this.redrawFeedback());
    });
  }

  createCustomDivs() {
    const options = { fontSize: '2vw', textAlign: 'center' };
    const div = this.createDiv('answerDiv', options, false);
    div.innerHTML = 'Do Re Mi Fa Sol';
    this.displayDiv.insertBefore(div, this.lettersDiv);

    this.layout.answerDiv = {
      div,
      name: 'answerDiv',
      percent: 15,
      visible: true,
      callback: this.sizeTextDiv.bind(this),
    };
  }

  /**
   * Only available in testMode, deletes the last inputted scale degree
   */
  protected deleteAnswer() {
    if (!this.options.testMode) throw Error('Non-testMode should not be able to call deleteAnswer');
    if (this.answerStack.length < 1) return;
    this.answerStack.pop();
    this.updateAnswerDiv();
  }

  addListeners() {
    const buttonHandler = (input) => {
      switch (input) {
        case 'play':
          this.play();
          break;
        case 'playTonic':
          this.playTonic();
          break;
        case 'delete':
          this.deleteAnswer();
          break;
        case 'check':
          this.checkStaffAnswer();
          break;
        default:
          this.checkAnswer(input);
      }
    };
    this.letterPanel.addAccidentalCallback((accidental) => {
      if (!this.accidentalsVisible) return;
      this.editor.setAccidental(accidental);
    }, this);
    this.letterPanel.addCustomButtonCallback(buttonHandler, this);
  }

  startGame() {
    this.gameStarted = true;
    this.displayInstructions();
    this.letterPanel.solfege.setSolfegeButtons(this.key.quality, this.levelData.degrees);
    this.resize();
  }

  displayInstructions(disableAudio = false) {
    super.displayInstructions(disableAudio);
    this.updateAnswerDiv();
  }

  updateAnswerDiv() {
    const mode = this.key.quality;
    this.answerDiv.setAttribute('aria-live', this.answerStack.length ? 'polite' : 'off');
    const answerDegrees = this.levelData.firstNoteGiven
      ? [this.currentQuestion.degrees[0], ...this.answerStack.map((a) => a.answer)]
      : this.answerStack.map((a) => a.answer);
    this.answerDiv.innerHTML = this.currentQuestion.degrees.reduce(
      (prior, current, index): string => {
        if (index !== 0) prior += ' ';
        if (index < answerDegrees.length) {
          const answerText = `${SolfegeSystem.degreeToSolfege(
            answerDegrees[index],
            mode,
            this.system
          )}`;
          prior += answerText;
        } else {
          prior += '___';
        }
        return prior as string;
      },
      ''
    ) as string;
  }

  checkAnswer(answer: ScaleDegree) {
    if (this.rightAnswerDelay) return;
    if (this.mode === 'staff') return;
    const degree = this.currentQuestion.degrees[this.questionPartCount + this.firstNoteGivenOffset];
    const correct = compareDegrees(degree, answer);

    if (this.options.testMode) {
      if (this.answerStack.length < this.currentQuestion.degrees.length) {
        this.questionPartCount++;
        this.answerStack.push({
          answer,
          correct,
          correctAnswer: degree,
        });
        this.updateAnswerDiv();
      }
      return;
    }

    if (correct) {
      let delay = 500;
      if (this.questions[this.questionNumber + 1] && this.questions[this.questionNumber + 1].key) {
        delay = 1000;
      }
      this.answerStack.push({ answer });
      this.rightAnswer(delay);
    } else {
      this.wrongAnswer();
    }
  }

  checkStaffAnswer() {
    if (this.rightAnswerDelay) return;
    // Checks no NoteHolders are ghosting
    const someNotesUnanswered = this.currentQuestion.notes.find((q, i) => {
      const isEmpty = this.staves.every((staff) => staff.noteHolders[i].length === 0);
      return isEmpty;
    });

    if (someNotesUnanswered && !this.options.testMode) {
      this.checkMark.warn('There are some notes missing');
      return;
    }

    let answer: Note[];
    try {
      answer = this.dissolveNotes();
    } catch (e) {
      console.error(e);
      this.checkMark.warn('There is more than one note per beat');
      return;
    }

    this.feedback = [];
    const { rightAnswers, wrongAnswers, answerStatusArr, answerIsRelative } =
      this.processStaffAnswer(answer);

    const correct = answerStatusArr.every((status) => status === true);
    // Determines if any note is correct but off by octave
    answerStatusArr.forEach((status, i) => {
      if (status === 'octaveDisplacement') {
        this.updateFeedbackText(i, 'check 8ve');
      }
      this.editor.renderer.render();
    });

    // For test mode and non-test, as long as there is one wrong octave, player gets to try til octave is correct.
    if (wrongOctaveOnly(answerStatusArr)) {
      if (!this.options.testMode) this.trackStaffAnswer(rightAnswers, wrongAnswers);
      this.colorWrongNotes();
      return;
    }

    // For non-test mode
    // If correct, right
    if (correct) {
      this.trackStaffAnswer(rightAnswers, wrongAnswers);
      this.rightStaffAnswer(1000);
      return;
    }

    this.wrongAnswerCount++;

    console.log({ answerStatusArr });
    // Annotate each notes for the mistake that they currently have
    answerStatusArr.forEach((status, i) => {
      switch (status) {
        case 'accidental':
          this.updateFeedbackText(i, 'wrong accidental');
          break;
        case 'low':
          this.updateFeedbackText(i, 'too low');
          break;
        case 'high':
          this.updateFeedbackText(i, 'too high');
          break;
      }
    });

    // If the player were possibly thinking of the relative key, let them know
    if (answerIsRelative) {
      this.wrongStaffAnswer(
        `Were you thinking of ${this.currentKey.getRelativeKey()}
        instead of ${this.currentKey}?`,
        rightAnswers,
        wrongAnswers
      );
      return;
    }

    this.wrongStaffAnswer(undefined, rightAnswers, wrongAnswers);
  }

  /**
   * Updates the progress tracker with number of correct and incorrect answers
   */
  protected trackStaffAnswer(rightAnswers: number, wrongAnswers: number) {
    if (!rightAnswers && !wrongAnswers) return;
    const trackerObject = {
      skill: this.currentQuestion.writingSolfegeSkill,
      questions: rightAnswers + wrongAnswers,
      right: rightAnswers,
      wrong: wrongAnswers,
    };
    this.progressTracker.multipleAnswers([trackerObject], rightAnswers + wrongAnswers, false);
  }

  getScaleAndFirstWrongDegree(): { degree: string; scale: Scale } {
    const keyQuality = this.key.quality;
    let quality: string = keyQuality;
    let degree = this.currentQuestion.degrees[this.questionPartCount + this.firstNoteGivenOffset];
    if (degree === 'M6') {
      quality = 'melodic minor';
      degree = '6';
    }
    if (degree === 'M7') {
      quality = 'harmonic minor';
      degree = '7';
    }

    const scale = new Scale(this.key.tonic, quality as ModeAlias);
    return { scale, degree };
  }

  rightAnswer(delay?: number) {
    this.wrongAnswerCount = 0;
    ++this.questionPartCount;
    this.updateAnswerDiv();

    // Only show check when all notes are listed:
    if (
      this.questionPartCount + this.firstNoteGivenOffset ===
      this.currentQuestion.degrees.length
    ) {
      this.questionPartCount = 0;
      // If we're in solfege mode, now make them do it on the staff:
      if (this.mode === 'solfege') {
        utils.wait(1).then(() => this.switchMode('staff'));
        this.checkMark.check();
        this.progressTracker.rightAnswer(this.currentQuestion.skills);
      } else {
        super.rightAnswer(delay);
      }
    } else {
      this.progressTracker.rightAnswer(this.currentQuestion.skills, false);
    }
  }

  rightStaffAnswer(delay?: number) {
    this.wrongAnswerCount = 0;
    this.questionPartCount = 0;

    // Staff progress was logged in checkStaffAnswer -- which uses
    // currentQuestion.writingSolfegeSkill instead of currentQuestion.skills
    // so we don't log it here.
    super.rightAnswer(delay, { noProgressTracker: true });
    utils.wait(delay / 1000).then(() => {
      if (!this.gameIsOver(true)) this.switchMode('solfege');
    });
  }

  wrongStaffAnswer(warningText = '', rightAnswers?: number, wrongAnswers?: number) {
    if (warningText) this.checkMark.xAndWarn(warningText);
    else this.checkMark.x();
    this.trackStaffAnswer(rightAnswers, wrongAnswers);
    this.colorWrongNotes();
  }

  get accidentalsVisible(): boolean {
    return (
      this.mode === 'staff' &&
      this.currentQuestion.mode !== 'Major' &&
      this.currentQuestion.mode !== 'naturalMinor'
    );
  }

  switchMode(mode: 'solfege' | 'staff' | 'gameOver') {
    this.mode = mode;
    const accidentalsMode = this.accidentalsVisible ? 'inline-block' : 'none';
    if (mode === 'solfege') {
      this.editor.keyboardDisabled = true;
      this.layout.editor.visible = false;
      this.letterPanel.solfege.show();
      this.layout.answerDiv.visible = true;
      this.letterPanel!.namedCustomButtons.checkAnswerButton!.style.display = 'none';
      this.letterPanel.accidentalButtons.forEach((button) => {
        button.style.display = accidentalsMode;
      });
      this.setQuestionPrompt();
      this.resize();
    } else if (mode === 'staff') {
      this.initializeStaffQuestion();
      this.progressTracker.start(
        this.getTargetSpeedFromSkills([this.currentQuestion.writingSolfegeSkill])
      );
      this.editor.keyboardDisabled = false;
      this.layout.editor.visible = true;
      this.letterPanel.solfege.hide();
      this.layout.answerDiv.visible = false;
      this.letterPanel!.namedCustomButtons.checkAnswerButton!.style.display = 'inline-block';
      this.letterPanel.accidentalButtons.forEach((button) => {
        button.style.display = accidentalsMode;
      });
      this.setQuestionPrompt();
      this.resize();
    } else {
      this.editor.disableEditor();
    }
  }

  gameOver() {
    this.updateInstructions('Nice work!');
    this.switchMode('gameOver');
    this.answerDiv.innerHTML = '';
    super.gameOver();
  }

  /**
   * Protected method that returns staves and note holders as specified
   */
  protected makeStaves() {
    const notesNumber = this.currentQuestion ? this.currentQuestion.notes.length : 1;
    if (this.options.level >= 9) {
      const staveNames: ClefName[] = ['treble', 'bass'];
      const staves = staveNames.map((clefString) => this.makeStaff(clefString, notesNumber));
      staves[0].noteHolders.forEach((nh, i) => {
        const nh2 = staves[1].noteHolders[i];
        nh.editable.link(nh2.editable, { maxNotes: 1, restIfEmptyAndLinkedFull: true });
      });
      return staves;
    }

    if (this.optionSet('clefs')) {
      return this.options.clefs.map((clefString) => this.makeStaff(clefString, notesNumber));
    }

    const clefs = this.currentKey.scale.prefersClef();
    const clef = clefs[~~(Math.random() * clefs.length)];
    return [this.makeStaff(clef, notesNumber)];
  }

  /**
   *  Chooses the right clef for the octave of solfege that will be played
   */
  initializeMusic() {
    const keySig = new KeySignature('C', 'Major');
    this.currentKey = keySig;
    this.staves = this.makeStaves();

    const timeSig = new TimeSignature('4/4');
    timeSig.hidden = true;

    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([keySig, timeSig, staff.staff, staff.voice]),
      []
    );
    this.music = new Music(musicElements);
  }

  generateQuestions(questions?: string[]): SolfegeQuestionObject[] {
    // The second argument limits octave displacement to within staff
    return super.generateQuestions(questions, true);
  }

  /**
   * Clear the noteHolder, re-render the notation
   * Set the key signature for the key of the music being played and generate new question
   */
  newQuestion(disableAudio?: any) {
    super.newQuestion(disableAudio);
    if (this.currentQuestion.key) this.currentKey = this.currentQuestion.key;
    this.switchMode('solfege');
  }

  /**
   * Detects number of notes and key signature of new question and initialize noteHolders, music, etc accordingly
   */
  protected initializeStaffQuestion() {
    this.wrongAnswerCount = 0;
    this.staves = this.makeStaves();

    // Initialize priorCorrect to all wrong
    this.priorCorrect = this.currentQuestion.notes.map(() => false);

    const notesCount = this.currentQuestion.notes.length;

    if (this.currentQuestion.key) {
      this.currentKey = this.currentQuestion.key;
    }

    const width = 60 + Math.abs(this.currentKey.accidentalsCount) * 10 + notesCount * 50;
    this.editor.options.fixedWidth = width;
    this.editor.renderer.options.fixedWidth = width;

    this.editor.renderer.render();

    // Label the solfege...
    this.staves[0].noteHolders.forEach((nh, i) => {
      const text = this.solfegeSystem.degreeToSolfege(
        this.currentQuestion.degrees[i],
        this.currentQuestion.mode === 'Major' ? 'Major' : 'minor'
      );
      const classs = this.solfegeSystem.system === 'scaleDegrees' ? 'accidentals' : '';
      nh.annotation = new Annotation(text, {
        justification: 'center',
        verticalJustification: 'bottom',
        class: classs,
      });
    });

    // Sets key for each noteHolder in each staff
    this.staves.forEach((staff) =>
      staff.noteHolders.forEach((noteHolder) =>
        noteHolder.editable.setKeySignature(this.currentKey)
      )
    );

    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([this.currentKey, staff.staff, staff.voice]),
      []
    );

    this.music = new Music(musicElements);
    this.editor.renderer.music = this.music;
    this.editor.renderer.render();
  }

  /**
   * Get Notes from different staves in the same verticality into one Note, while
   * maintaining the original Notes
   */
  protected dissolveNotes() {
    const notesNumber = this.currentQuestion.notes.length;
    const notes: Note[] = [];
    // index i keeps track of the current verticality. e.g. beat 1, i == 0; beat 2, i == 1
    for (let i = 0; i < notesNumber; i++) {
      let note: Note;
      this.staves.forEach((staff) => {
        // More than one note in same verticality same staff
        if (staff.noteHolders[i].notes.length > 1) {
          throw Error('More than one note per verticality');
        }
        // More than one note in same verticality different staff
        if (note && !staff.noteHolders[i].isEmpty()) {
          throw Error('More than one note per verticality');
        }
        // One note per verticality
        if (!staff.noteHolders[i].isEmpty()) {
          note = staff.noteHolders[i].notes[0];
        }
      });
      notes.push(note);
    }
    return notes;
  }

  /**
   * Returns an array with the status of each note:
   * true: correct
   * false: incorrect with no clues (before 2 attempts have been used)
   * 'octaveDisplacement': octave displacement (given even in the first attempt)
   * 'low': too low
   * 'high': too high
   * 'accidental': wrong accidental
   * --------------------------------
   * this.answerIsRelative is set to true if too many notes (>75%) are in the relative key
   */
  protected processStaffAnswer(answer: Note[]) {
    const status: AnswerStatus[] = [];
    let rightAnswers = 0;
    /** Number of notes that might have been mistaken to be in the relative key. */
    let numNotesRelative = 0;
    const relativeKey = this.currentKey.getRelativeKey();

    // Evaluate each note
    for (let i = 0; i < this.currentQuestion.notes.length; i++) {
      const answerNote = answer[i];
      // In testMode, it is possible for a note to be missing and the answer still be processed
      if (answerNote === undefined) {
        status.push(false);
        continue;
      }
      const correctNote = this.currentQuestion.notes[i];

      const correctIgnoreOctave = answerNote.equalsIgnoreOctave(correctNote);
      // if note name and accidental is correct, record as correct answer
      if (correctIgnoreOctave && !this.priorCorrect[i]) {
        this.priorCorrect[i] = true;
        rightAnswers++;
      }
      const octaveDisplacement = correctIgnoreOctave && !answerNote.equals(correctNote);

      if (octaveDisplacement) {
        status.push('octaveDisplacement');
        continue;
      }

      if (correctIgnoreOctave) {
        status.push(true);
        continue;
      }

      if (this.wrongAnswerCount === 0) {
        status.push(false);
        continue;
      }

      const relativeNote = relativeKey.getNoteAtScaleDegreeOrThrow(this.currentQuestion.degrees[i]);
      if (answerNote.letter === relativeNote.letter) numNotesRelative++;

      if (answerNote.letter === correctNote.letter) {
        status.push('accidental');
        continue;
      }

      if (answerNote.isLessThan(correctNote)) {
        status.push('low');
        continue;
      }

      status.push('high');
    }

    // Assume answer is not relative
    let answerIsRelative = false;
    // Only check for relative-key-mistake if this is the first question of key and the user
    // already got one wrong.
    if (this.currentQuestion.key && this.wrongAnswerCount > 0) {
      // percentRelative is the fraction of the notes that might have been mistaken to be in
      // the relative key
      const percentRelative = numNotesRelative / this.currentQuestion.notes.length;
      // 75% is the threshold to say that the user might have mistaken the key for the its relative key
      if (percentRelative >= 0.75) answerIsRelative = true;
    }

    const wrongAnswers = this.priorCorrect.reduce((total, correct) => total + (correct ? 0 : 1), 0);
    return { rightAnswers, wrongAnswers, answerStatusArr: status, answerIsRelative };
  }

  /**
   * Color the all wrong notes red
   */
  protected colorWrongNotes() {
    if (this.wrongAnswerCount < 1) return;

    for (let i = 0; i < this.currentQuestion.notes.length; i++) {
      const questionNote = this.currentQuestion.notes[i];
      this.staves.forEach((staff) => {
        const nh = staff.noteHolders[i];
        if (!nh.isEmpty()) {
          const answerNote = nh.notes[0];
          const correct = answerNote.equalsIgnoreOctave(questionNote);
          if (correct) delete nh.color;
          else nh.color = 'red';
        }
      });
    }
    this.editor.renderer.render();
  }

  /**
   * Adds feedback annotation to a wrong noteHolder. If no text is supplied, go with the current feedback
   */
  protected updateFeedbackText(
    noteHolderIndex: number,
    text = this.feedback[noteHolderIndex] || ''
  ) {
    // for each text feedback, do this:
    let staff: {
      staff: Staff;
      voice: Voice;
      noteHolders: NoteHolder[];
      clef: Clef;
    };

    if (this.staves[1] && !this.staves[1].noteHolders[noteHolderIndex].isEmpty()) {
      staff = this.staves[1];
    } else staff = this.staves[0];

    const noteHolder = staff.noteHolders[noteHolderIndex];
    if (!noteHolder || noteHolder.length === 0) return;

    const { x, w, y, h } = noteHolder.staveNote.getBoundingBox();
    const centerX = x + w / 2;
    const bottom = y + h;
    const line2 = noteHolder.staveNote.getStave().getYForLine(2);
    const above = bottom - line2 > line2 - y;
    const textY = above
      ? Math.min(noteHolder.staveNote.getStave().getYForLine(-1), y - 10)
      : Math.max(noteHolder.staveNote.getStave().getYForLine(5), bottom + 10);
    const { ctx } = this.editor.renderer;
    ctx.save();
    ctx.attributes['text-anchor'] = 'middle';
    ctx.attributes.fill = 'red';
    this.feedback[noteHolderIndex] = text;

    this.editor.renderer.ctx.fillText(text, centerX, textY);
    ctx.restore();
  }

  redrawFeedback() {
    this.currentQuestion.notes.forEach((__, i) => this.updateFeedbackText(i));
  }

  /**
   * Make the staff/staves and noteHolders for each new question
   */
  protected makeStaff(clefName: ClefName, notesNumber: number) {
    const clef = new Clef(clefName);
    const staff = new Staff(clef);
    const measure = new Measure();
    const noteHolders: NoteHolder[] = [];

    for (let i = 0; i < notesNumber; i++) {
      const noteHolder = new NoteHolder('', 'w');
      noteHolder.editable = new Editable(noteHolder, {
        pitch: true,
        accidental: false,
        deleteable: ['placeHolderRest'],
        selectable: true,
      });

      const clearFeedback = () => {
        noteHolder.color = undefined;
        this.updateFeedbackText(i, '');
      };
      noteHolder.editable.eventHandler.addListener('pitchChange', clearFeedback);
      noteHolder.editable.eventHandler.addListener('accidentalChange', clearFeedback);
      measure.push(noteHolder);
      noteHolders.push(noteHolder);
    }
    const voice = new Voice([measure]);
    voice.setStaff(staff);
    return {
      staff,
      voice,
      clef,
      noteHolders,
    };
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(): AnswerAndData {
    const answerStack = this.currentQuestion.degrees.map((degree, i) => {
      const hasAnswer = !!this.answerStack[i];
      const answerObj = this.answerStack[i];
      const text = hasAnswer
        ? this.solfegeSystem.degreeToSolfege(answerObj.answer, this.key.quality)
        : '__';
      const answer = hasAnswer ? text : '__';
      return {
        ...(this.answerStack[i] || {}),
        text,
        answer,
        correct: hasAnswer ? answerObj.correct : false,
        correctAnswer: degree,
      };
    });

    const rightAnswers = answerStack.reduce((prior, q) => prior + Number(q.correct) * 1, 0);
    const wrongAnswers = answerStack.length - rightAnswers;

    const question = this.currentQuestion.degrees.reduce((prior, deg) => {
      const degInSystem = `${this.solfegeSystem.degreeToSolfege(
        deg,
        this.currentQuestion.key.quality
      )}`;
      return prior + (prior ? ' ' : '') + degInSystem;
    }, '');

    const studentAnswerText = answerStack.reduce(
      (prior, ans) => prior + (prior ? ' ' : '') + ans.text,
      ''
    );

    const answerAndData = {
      studentAnswerText,
      rightAnswers,
      wrongAnswers,
      question: `${question}`,
      questionType: 'MultipleNoteSolfegeID',
      questionText: `Name the ${this.solfegeSystem} you hear.`,
      questionSVG: null,
      studentAnswerSVG: null,
      correctAnswerText: `${question}`,
      skills: [this.currentQuestion.skills],
    };
    return answerAndData;
  }

  static registry: InteractiveRegistryEntry[] = [
    {
      description: 'Dictate solfege, then write it on the staff',
      options: {
        levelFromUserData: true,
        tonicizeEvery: 4,
      },
      skills: [
        'sd_frag_123_major',
        'sd_frag_123_minor',
        'sd_frag_12345_major',
        'sd_frag_12345_minor',
        'sd_frag_5678_major',
        'sd_frag_5678_minor',
        'sd_frag_5678_all_minors',
        'sd_frag_1_to_8_major',
        'sd_frag_1_to_8_minor',
        'sd_frag_1_to_8_major_w_octaves',
        'sd_frag_1_to_8_minor_w_octaves',
      ],
    },
  ];
}
